import { pushSwAuthDebugEvent } from './swAuthDebugBuffer.ts'
import type {
  SwAuthReloadMismatchKind,
  SwAuthSyncMetricSnapshot,
} from './swAuthSyncMetricsCore'

export const SW_AUTH_TELEMETRY_MESSAGE_TYPE = 'SW_AUTH_TELEMETRY' as const

export type SwAuthTelemetryEvent =
  | {
      kind: 'sync_posted'
      matched: boolean
      waitMs: number
      hasEpoch: boolean
      preflightReadFailed?: boolean
      metrics: SwAuthSyncMetricSnapshot
    }
  | {
      kind: 'reload_ok'
      attempt: number
      metrics: SwAuthSyncMetricSnapshot
    }
  | {
      kind: 'reload_mismatch_retry'
      attempt: number
      mismatchKind: SwAuthReloadMismatchKind
      metrics: SwAuthSyncMetricSnapshot
    }
  | {
      kind: 'reload_mismatch_exhausted'
      mismatchKind: SwAuthReloadMismatchKind
      metrics: SwAuthSyncMetricSnapshot
    }
  | {
      kind: 'reload_without_epoch'
      metrics: SwAuthSyncMetricSnapshot
    }
  | {
      kind: 'idb_read_failed'
      message: string
      metrics: SwAuthSyncMetricSnapshot
    }

type MainTelemetrySink = (event: SwAuthTelemetryEvent) => void

let mainTelemetrySink: MainTelemetrySink | null = null

export function registerSwAuthTelemetryMainSink(sink: MainTelemetrySink): void {
  mainTelemetrySink = sink
}

function isServiceWorkerContext(): boolean {
  return (
    typeof ServiceWorkerGlobalScope !== 'undefined' &&
    typeof self !== 'undefined' &&
    self instanceof ServiceWorkerGlobalScope
  )
}

function broadcastTelemetryToWindowClients(event: SwAuthTelemetryEvent): void {
  if (!isServiceWorkerContext()) return
  const sw = self as unknown as ServiceWorkerGlobalScope
  void sw.clients.matchAll({ type: 'window', includeUncontrolled: true }).then((clients) => {
    for (const client of clients) {
      client.postMessage({ type: SW_AUTH_TELEMETRY_MESSAGE_TYPE, event })
    }
  })
}

/** Forwards SW auth telemetry to the page (Sentry) or from SW to controlled clients. */
export function emitSwAuthTelemetry(event: SwAuthTelemetryEvent): void {
  if (typeof window !== 'undefined' && mainTelemetrySink) {
    pushSwAuthDebugEvent(event)
    mainTelemetrySink(event)
    return
  }
  broadcastTelemetryToWindowClients(event)
}

export function handleSwAuthTelemetryMessage(data: unknown): boolean {
  if (
    typeof data !== 'object' ||
    data === null ||
    (data as { type?: unknown }).type !== SW_AUTH_TELEMETRY_MESSAGE_TYPE
  ) {
    return false
  }
  const event = (data as { event?: unknown }).event
  if (!event || typeof event !== 'object' || !('kind' in event)) {
    return false
  }
  const typed = event as SwAuthTelemetryEvent
  pushSwAuthDebugEvent(typed)
  mainTelemetrySink?.(typed)
  return true
}
